import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { TranslationService } from "../../services/TranslationService";
import { TableHeaderWithFieldId } from "../shared/Table";
import { formatDateTimeDigits } from "../../utils/FormatUtils";
import { InboxContext, QuickFilterType } from "./InboxTableProvider";
import { useHistory } from "react-router-dom";
import { parseDate, restTimezone } from "../../utils/ParseUtils";
import TableContext from "../task/TableContext";
import ActivityService from "../../services/ActivityService";
import { ToastService } from "../shared/bootstrap/Toast";
import ModalService from "../shared/bootstrap/modal/Modal";
import { EmailEdit } from "../client/activity/components/EmailEdit";
import { EmailCompose } from "../client/components/EmailCompose";
import TooltipComponent from "../shared/TooltipComponent";
import { Tooltip } from "bootstrap";

import {
  forwardAtt,
  replyAtt
} from "../client/activity/components/ActivityEmailDisplay";
import { MarkUnreadIcon } from "./InboxList";
import { EmailAICopilotPanelWithLoading } from "../shared/components/EmailAICopilotPanel";
import { addIf, OptionalMap } from "../../utils/Utils";
import CounterMsgContext from "../shared/CounterMsgContext";
import { SkeletonLoader } from "../../components/skeletonLoader/SkeletonLoader";
import { openWhatsappPanel } from "../../components/whatsapp/utils/openWhatsappPanel";
import FloatingPanelService from "../shared/FloatingPanel";
import CompanyService from "../../services/CompanyService";
import { AIAgentType } from "../../entities/company/AIBilling/AIBilling";
import { SafeHtml } from "../../components/SafeHtml";
import { useWinStore } from "../../components/windows/windowStore";
import {
  buildReplyAllRecipients,
  buildReplyRecipients
} from "../../utils/emailReplyAll";

export const InboxTableTableColumn = ({
  data,
  header,
  loadingData,
  setLoadingData
}: {
  data: any;
  header: TableHeaderWithFieldId;
  loadingData: boolean;
  setLoadingData: (val: boolean) => void;
}) => {
  const { getCounterInbox } = useContext(CounterMsgContext);
  const { request } = useContext(InboxContext);
  const { reload } = useContext(TableContext);
  const { open } = useWinStore.getState();

  const getValue = () => data[header.fieldId];
  const value = getValue();
  const navigate = useHistory();
  const actionsRef = useRef<any[]>([]);
  const getMailField = (primary: string, fallback: string) =>
    (data?.[primary] as string | undefined) ??
    (data?.[fallback] as string | undefined);
  const openNewWhatsappModal_mixto = async () => {
    // MIGRADO: Ahora usa WindowStore para ventanas persistentes
    await openWhatsappPanel({
      personId: data.PersonId,
      isNewConversation: !data.Chat,
      historyMessageId: data.ID,
      onSubmit: reload
    });
  };
  const reply = () => {
    const recipients = buildReplyRecipients({
      from: getMailField("From", "from"),
      currentUserEmail: CompanyService.getCurrentUserEmails()
    });

    openEmailModal(
      "RE: " + data.Subject,
      recipients.to,
      data.groupId,
      data.groupName,
      replyAtt
    );
  };

  const replyAll = () => {
    const recipients = buildReplyAllRecipients({
      from: getMailField("From", "from"),
      to: getMailField("To", "to"),
      cc: getMailField("cc", "CC"),
      bcc: getMailField("bcc", "BCC"),
      currentUserEmail: CompanyService.getCurrentUserEmails()
    });

    openEmailModal(
      "RE: " + data.Subject,
      recipients.to,
      data.groupId,
      data.groupName,
      replyAtt,
      recipients.cc,
      recipients.bcc
    );
  };

  const forward = () => {
    openEmailModal(
      "FW: " + data.Subject,
      undefined,
      data.groupId,
      data.groupName,
      forwardAtt
    );
  };

  const openEmailModal = async (
    subject: string,
    to?: string,
    groupId?: string,
    groupName?: string,
    replyAtt?: string,
    CC?: string,
    BCC?: string
  ) => {
    try {
      const onSubmit = () => reload();
      await ActivityService.setRead({ ids: [data.ID], read: "1" });
      const { windows, close } = useWinStore.getState();
      const copilotWindow = windows.find(
        (w) => w.identifier === `copilot-${data.ID}`
      );
      if (copilotWindow) {
        close(copilotWindow.id);
      }
      FloatingPanelService.showPanel({
        title: TranslationService.translate.EmailToClient,
        children: (
          <EmailCompose
            personId={data.PersonId.toString()}
            defaultSubject={subject}
            defaultTo={to}
            defaultCC={CC}
            defaultBCC={BCC}
            defaultGroupId={groupId}
            defaultGroupLabel={groupName}
            prevMessageId={data.ID.toString()}
            onSubmit={onSubmit}
            reply={replyAtt}
          />
        ),
        width: 900,
        height: 669
      });
    } catch (error) {
    } finally {
      await getCounterInbox();
      await reload();
    }
  };

  const replyAction = () => ({
    icon: "fa-reply",
    onClick: reply,
    tooltip: TranslationService.translate.Reply
  });
  const replyAllAction = () => ({
    icon: "fa-reply-all",
    onClick: replyAll,
    tooltip: TranslationService.translate.ReplyAll
  });
  const forwardAction = () => ({
    icon: "fa-arrow-alt-right",
    onClick: forward,
    tooltip: TranslationService.translate.Foward
  });

  const openEmailWithAssistant = useCallback(async () => {
    try {
      FloatingPanelService.hidePanel();

      const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      const combinedWidth = Math.min(vw - 40, 1240);
      const combinedHeight = vh - 60;
      const x = Math.max(16, (vw - combinedWidth) / 2);
      const y = Math.max(16, (vh - combinedHeight) / 2);

      open({
        title: data.Subject || TranslationService.translate.EmailToClient,
        content: (
          <EmailAICopilotPanelWithLoading
            emailId={data.ID}
            personId={data.PersonId}
            actions={actionsRef.current}
            reload={reload}
            onCopilotComplete={async () => {
              await getCounterInbox();
              await reload();
            }}
          />
        ),
        width: combinedWidth,
        height: combinedHeight,
        x,
        y,
        noHeader: true,
        identifier: `copilot-${data.ID}`
      });
    } catch (error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } finally {
      await getCounterInbox();
      !data.Read && (await reload());
    }
  }, [
    data.ID,
    data.Subject,
    data.PersonId,
    data.Read,
    open,
    reload,
    getCounterInbox
  ]);

  // Elementos de esta celda con tooltip creado, para poder limpiarlos si la celda se desmonta
  const tooltipElemsRef = useRef<Set<HTMLElement>>(new Set());

  const disposeTooltips = useCallback(() => {
    tooltipElemsRef.current.forEach((elem) => {
      try {
        Tooltip.getInstance(elem)?.dispose();
      } catch (e) {
        // Ignorar errores si el tooltip ya fue eliminado
      }
    });
    tooltipElemsRef.current.clear();
  }, []);

  // Al pasar a skeleton (loadingData) o desmontar la fila, el span desaparece sin disparar
  // mouseleave: se eliminan los tooltips para que no queden huérfanos en el body
  useEffect(() => disposeTooltips, [loadingData, disposeTooltips]);

  // Si se pasa `forcedText` el tooltip se muestra siempre; si no, solo cuando el texto está truncado
  function showTooltip(elem: HTMLElement, forcedText?: string) {
    const isTruncated = elem.scrollWidth > elem.clientWidth;
    const text = (forcedText ?? elem.textContent ?? "").trim();

    if (text && (forcedText !== undefined || isTruncated)) {
      const tip = Tooltip.getOrCreateInstance(elem, {
        trigger: "manual",
        container: "body",
        customClass: "tooltipIntiza"
      });
      tooltipElemsRef.current.add(elem);
      elem.setAttribute("data-bs-original-title", text);
      (tip as any).setContent?.({ ".tooltip-inner": text });

      tip.show();
    } else {
      hideTooltip(elem);
    }
  }

  function hideTooltip(elem: HTMLElement) {
    Tooltip.getInstance(elem)?.dispose();
    tooltipElemsRef.current.delete(elem);
    elem.removeAttribute("data-bs-original-title");
  }

  const sparkleActionForEmail = useCallback(
    () => ({
      icon: "emoji:✨",
      onClick: openEmailWithAssistant
    }),
    [openEmailWithAssistant]
  );

  const sparkleActionRow = () => ({
    icon: "emoji:✨",
    onClick: openEmailWithAssistant
  });

  const markAsUnread = useCallback(() => {
    const { translate } = TranslationService;
    ModalService.showDefaultModal({
      title: translate.MarkAsNotRead,
      message: translate.AreYouSureMarkAsNotReadMessage,
      acceptButtonLabel: translate.MarkAsNotRead,
      onAcceptClick: async () => {
        const result = await ActivityService.setRead({
          ids: [data.ID],
          read: "0"
        });
        if (result instanceof Error) {
          ToastService.showToast(
            translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return;
        }
        FloatingPanelService.hidePanel();
        await getCounterInbox();
        await reload();
      }
    });
  }, [data.ID, getCounterInbox, reload]);

  const canActOnMessage = CompanyService.canActOnMessage();

  const actionsForEmail = useMemo(
    () => {
      const actionsArray = [
        ...addIf(
          canActOnMessage &&
            CompanyService.canShowAgentAI(AIAgentType.Categorize),
          sparkleActionForEmail()
        ),
        ...(canActOnMessage
          ? [
              replyAction(),
              replyAllAction(),
              forwardAction(),
              {
                icon: MarkUnreadIcon,
                onClick: markAsUnread,
                tooltip: TranslationService.translate.MarkAsNotRead
              }
            ]
          : [])
      ];
      actionsRef.current = actionsArray;
      return actionsArray;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sparkleActionForEmail, markAsUnread, canActOnMessage]
  );

  const rowActions = [
    ...addIf(
      canActOnMessage && CompanyService.canShowAgentAI(AIAgentType.Categorize),
      sparkleActionRow()
    ),
    ...(canActOnMessage
      ? [replyAction(), replyAllAction(), forwardAction()]
      : [])
  ];

  const editEmailModal = useCallback(async () => {
    try {
      setLoadingData(true);
      await ActivityService.setRead({ ids: [data.ID], read: "1" });
      const emailRequest = await ActivityService.get(data.ID).finally(() =>
        setLoadingData(false)
      );
      if (emailRequest instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }

      FloatingPanelService.showPanel({
        title: emailRequest.item.Subject,
        children: (
          <EmailEdit email={emailRequest.item} actions={actionsForEmail} />
        ),
        width: 700,
        height: 500
      });
    } finally {
      await getCounterInbox();
      !data.Read && (await reload());
    }
  }, [
    setLoadingData,
    data.ID,
    data.Read,
    actionsForEmail,
    getCounterInbox,
    reload
  ]);

  const handleOpenModal = () => {
    disposeTooltips();
    switch (request.quickfilter) {
      case QuickFilterType.Whatsapp:
      case QuickFilterType.WhatsappInbound:
        openNewWhatsappModal_mixto();
        break;
      case QuickFilterType.Email:
        editEmailModal();
        break;
    }
  };

  const handleNavigateClient = async () => {
    disposeTooltips();
    setLoadingData(true);
    await navigate.replace(`client/details?id=${data.PersonId.toString()}`);
  };

  function decodeHTMLEntities(text: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");
    return doc.documentElement.textContent;
  }

  function truncateText(htmlText: string, maxLength: number) {
    const decodedText = decodeHTMLEntities(htmlText);
    return decodedText && decodedText?.length > maxLength
      ? decodedText.substring(0, maxLength) + "..."
      : decodedText;
  }

  if (loadingData) {
    return (
      <td>
        <SkeletonLoader />
      </td>
    );
  }

  // Obtener clase CSS de ancho basada en el header
  const getCellWidthClass = (): string => {
    if (header.className) {
      if (header.className.includes("inbox-content-column"))
        return "inbox-content-column";
      if (header.className.includes("inbox-subject-column"))
        return "inbox-subject-column";
      if (header.className.includes("th-w-120")) return "th-w-120";
      if (header.className.includes("th-w-160")) return "th-w-160";
      if (header.className.includes("w-200px")) return "w-200px";
      if (header.className.includes("w-300px")) return "w-200px"; // Reducido de 300px a 200px
      if (header.className.includes("w-120px")) return "w-100px"; // Reducido de 120px a 100px
      if (header.className.includes("w-100px")) return "w-100px";
      if (header.className.includes("w-40px")) return "w-40px";
    }
    return "";
  };

  const cellWidthClass = getCellWidthClass();
  const baseTdClass = `${header.className || ""} ${cellWidthClass}`.trim();

  switch (header.fieldId) {
    case "LastActivityDate":
    case "date":
      const dateValue = getValue();

      if (!dateValue) return <td></td>;

      const date = new Date(
        OptionalMap(parseDate(dateValue), (x) => restTimezone(x).toString()) ??
          ""
      );
      // Siempre mostrar fecha y hora
      const dateFormated = formatDateTimeDigits(date);
      return (
        <td className={`${baseTdClass} th-nowrap`} onClick={handleOpenModal}>
          {!data.Read ? <strong>{dateFormated}</strong> : dateFormated}
        </td>
      );
    case "personName":
      return (
        <td
          className={`${baseTdClass} th-truncate`}
          onClick={handleNavigateClient}
        >
          <span
            onMouseEnter={(e) => showTooltip(e.currentTarget)}
            onMouseLeave={(e) => hideTooltip(e.currentTarget)}
            onBlur={(e) => hideTooltip(e.currentTarget)}
          >
            {!data.Read ? <strong>{data.personName}</strong> : data.personName}
          </span>
        </td>
      );
    case "FromName":
      return (
        <td
          className={`${baseTdClass} th-truncate`}
          onClick={handleNavigateClient}
        >
          <span
            onMouseEnter={(e) =>
              showTooltip(e.currentTarget, data.From || undefined)
            }
            onMouseLeave={(e) => hideTooltip(e.currentTarget)}
            onBlur={(e) => hideTooltip(e.currentTarget)}
          >
            {!data.Read ? <strong>{data.FromName}</strong> : data.FromName}
          </span>
        </td>
      );
    case "Content":
      const isWhatsApp =
        request.quickfilter === QuickFilterType.Whatsapp ||
        request.quickfilter === QuickFilterType.WhatsappInbound;
      if (isWhatsApp) {
        // Para WhatsApp, mostrar el último mensaje truncado
        const content = truncateText(data.Content || "", 100);
        return (
          <td className="th-truncate" onClick={handleOpenModal}>
            {!data.Read ? (
              <strong>
                <SafeHtml
                  html={content || ""}
                  style={{ wordBreak: "break-all" }}
                />
              </strong>
            ) : (
              <SafeHtml
                html={content || ""}
                style={{ wordBreak: "break-all" }}
              />
            )}
          </td>
        );
      }
      return (
        <td className="th-truncate" onClick={handleOpenModal}>
          {!data.Read ? (
            <strong>
              <SafeHtml
                html={data.Content}
                style={{ wordBreak: "break-all" }}
              />
            </strong>
          ) : (
            <SafeHtml html={data.Content} style={{ wordBreak: "break-all" }} />
          )}
        </td>
      );
    case "Phone":
      return (
        <td className={`${baseTdClass} th-truncate`} onClick={handleOpenModal}>
          {!data.Read ? (
            <strong>{data.Phone || data.smsphone || ""}</strong>
          ) : (
            data.Phone || data.smsphone || ""
          )}
        </td>
      );
    case "Subject":
      // Decodificar HTML y mostrar solo texto plano
      const subjectText =
        decodeHTMLEntities(data.Subject || "") || data.Subject || "";
      return (
        <td className={`${baseTdClass} th-truncate`} onClick={handleOpenModal}>
          <span
            onMouseEnter={(e) => showTooltip(e.currentTarget)}
            onMouseLeave={(e) => hideTooltip(e.currentTarget)}
            onBlur={(e) => hideTooltip(e.currentTarget)}
          >
            {!data.Read ? <strong>{subjectText}</strong> : subjectText}
          </span>
        </td>
      );
    case "actions":
      return (
        <td className={baseTdClass}>
          {/* <div className="row justify-content-end show-when-hovering-parent"> */}
          <div className="actionsInbox floating-actions gap-3">
            {rowActions.map((action, index) => {
              const isEmoji =
                typeof action.icon === "string" &&
                action.icon.startsWith("emoji:");
              const emojiChar = isEmoji
                ? action.icon.replace("emoji:", "")
                : "";
              return (
                <TooltipComponent title={false} key={index}>
                  <button onClick={() => action.onClick()}>
                    {isEmoji ? (
                      <span style={{ fontSize: 16, lineHeight: 1, width: 20 }}>
                        {emojiChar}
                      </span>
                    ) : (
                      <i className={"far " + action.icon} />
                    )}
                  </button>
                </TooltipComponent>
              );
            })}
          </div>
          {/* </div> */}
        </td>
      );
    case "attachments":
      return (
        <td>
          {data.hasAttachments && data.files.length > 0 && (
            <div>
              <i className="far fa-fw fa-paperclip activity-icon-color my-2" />
            </div>
          )}
        </td>
      );
    default:
      return (
        <td className={`${baseTdClass} th-truncate`} onClick={handleOpenModal}>
          {!data.Read ? <strong>{value}</strong> : value}
        </td>
      );
  }
};
